import React from 'react';
import { MessageCircle, Heart, Stethoscope, BookOpen, Apple } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const Care = () => {
  const careTopics = [
    {
      icon: Heart,
      title: 'Puppy Care Basics',
      description:
        'Learn essential puppy care including feeding schedules, sleep requirements, and early socialization techniques for healthy development.'
    },
    {
      icon: Stethoscope,
      title: 'Vaccination Schedule',
      description:
        'Complete vaccination guide: DHPP, Rabies, and Bordetella. Follow the recommended schedule to keep your puppy protected from diseases.'
    },
    {
      icon: Apple,
      title: 'Nutrition Guide',
      description:
        'Proper nutrition is crucial. Feed high-quality puppy food 3-4 times daily. Avoid human food, especially chocolate, grapes, and onions.'
    },
    {
      icon: BookOpen,
      title: 'Training Tips',
      description:
        'Start training early with positive reinforcement. Focus on basic commands: sit, stay, come. Consistency and patience are key to success.'
    }
  ];

  const handleConsultation = () => {
    const message = 'Hello, I would like to consult about pet care guidance.';
    window.open(`https://wa.me/917217648679?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div data-testid="care-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
      <SEO />
      {/* Hero Section */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(to bottom right, #DCFCE7, #FFFFFF)',
          borderBottom: '1px solid var(--neutral-border)'
        }}
      >
        <div className="container mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            Pet Care Guidance
          </h1>
          <p className="text-lg" style={{ color: 'var(--text-muted)' }}>
            Expert advice to keep your furry friend healthy and happy
          </p>
        </div>
      </section>

      {/* Care Topics */}
      <section className="py-16">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {careTopics.map((topic, index) => {
              const Icon = topic.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="shadow-soft"
                  style={{
                    backgroundColor: 'white',
                    padding: '2rem',
                    borderRadius: '1.5rem'
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center mb-4"
                    style={{
                      width: '64px',
                      height: '64px',
                      backgroundColor: 'var(--accent-light)',
                      borderRadius: '50%'
                    }}
                  >
                    <Icon size={32} style={{ color: 'var(--accent-main)' }} />
                  </div>
                  <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--text-main)' }}>
                    {topic.title}
                  </h3>
                  <p style={{ color: 'var(--text-muted)', lineHeight: '1.8' }}>{topic.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Care Guide */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: 'var(--text-main)' }}>
              Complete Puppy Care Checklist
            </h2>

            <div className="space-y-8">
              {/* First Week */}
              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'var(--primary-light)', border: '2px solid var(--primary-main)' }}
              >
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--primary-dark)' }}>
                  First Week at Home
                </h3>
                <ul className="space-y-2" style={{ color: 'var(--text-main)' }}>
                  <li>• Create a safe, comfortable space for your puppy</li>
                  <li>• Establish feeding schedule (3-4 times per day)</li>
                  <li>• Schedule first vet checkup within 48 hours</li>
                  <li>• Begin house training with consistent routine</li>
                  <li>• Introduce basic commands with positive reinforcement</li>
                </ul>
              </div>

              {/* Feeding Guide */}
              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'var(--secondary-light)', border: '2px solid var(--secondary-main)' }}
              >
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--secondary-dark)' }}>
                  Feeding Guidelines
                </h3>
                <ul className="space-y-2" style={{ color: 'var(--text-main)' }}>
                  <li>• Feed high-quality puppy food formulated for their size</li>
                  <li>• 8-12 weeks: 4 meals per day</li>
                  <li>• 3-6 months: 3 meals per day</li>
                  <li>• 6+ months: 2 meals per day</li>
                  <li>• Always provide fresh water</li>
                </ul>
              </div>

              {/* Health Care */}
              <div
                className="p-6 rounded-2xl"
                style={{ backgroundColor: 'var(--accent-light)', border: '2px solid var(--accent-main)' }}
              >
                <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--accent-dark)' }}>
                  Health & Vaccination
                </h3>
                <ul className="space-y-2" style={{ color: 'var(--text-main)' }}>
                  <li>• 6-8 weeks: First DHPP vaccination</li>
                  <li>• 10-12 weeks: Second DHPP vaccination</li>
                  <li>• 14-16 weeks: Third DHPP + Rabies vaccination</li>
                  <li>• Regular deworming every 2-3 months</li>
                  <li>• Annual health checkups recommended</li>
                </ul>
              </div>

              {/* Training */}
              <div className="p-6 rounded-2xl" style={{ backgroundColor: '#EDE9FE', border: '2px solid #7C3AED' }}>
                <h3 className="text-xl font-bold mb-4" style={{ color: '#6D28D9' }}>
                  Training & Socialization
                </h3>
                <ul className="space-y-2" style={{ color: 'var(--text-main)' }}>
                  <li>• Start training early with short 5-10 minute sessions</li>
                  <li>• Use positive reinforcement (treats, praise)</li>
                  <li>• Socialize with other dogs and people safely</li>
                  <li>• Be consistent with commands and rules</li>
                  <li>• Crate training helps with house training</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="py-16" style={{ backgroundColor: 'var(--neutral-bg)' }}>
        <div className="container mx-auto text-center">
          <div
            className="inline-block p-12 rounded-3xl"
            style={{ backgroundColor: 'var(--primary-light)' }}
          >
            <h2 className="text-3xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
              Need Personalized Care Advice?
            </h2>
            <p className="mb-6" style={{ color: 'var(--text-muted)' }}>
              Our experts are here to help with any questions about your puppy's health and care
            </p>
            <button
              onClick={handleConsultation}
              data-testid="consultation-button"
              className="transition-all hover:-translate-y-1 flex items-center justify-center gap-2 mx-auto"
              style={{
                backgroundColor: 'var(--primary-main)',
                color: 'white',
                padding: '1rem 2rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0 10px 30px rgba(14, 165, 233, 0.3)'
              }}
            >
              <MessageCircle size={20} />
              Get Expert Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Care;